import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type AxiosError,
  type InternalAxiosRequestConfig,
  isAxiosError,
} from "axios";

// ─── 공개 타입 ──────────────────────────────────────────────────────────────

/** 목록 API 응답 공통 구조 */
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
}

/** 404 전용 에러 클래스 — store에서 instanceof로 구분 가능 */
export class NotFoundError extends Error {
  constructor(message = '요청한 리소스를 찾을 수 없습니다.') {
    super(message);
    this.name = 'NotFoundError';
  }
}

// ─── 내부 타입 ───────────────────────────────────────────────────────────────

interface ServerErrorResponseData {
  data?: { message?: string | null; [key: string]: unknown };
  message?: string;
  msg?: string;
  result?: { msg?: string; [key: string]: unknown };
  [key: string]: unknown;
}

// ─── API 서비스 ──────────────────────────────────────────────────────────────

const BASE_URL: string = import.meta.env.VITE_BASE_URL || "";

class ApiService {
  public axios: AxiosInstance;

  constructor() {
    this.axios = axios.create({
      baseURL: BASE_URL,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      timeout: 100000,
      withCredentials: false,
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    this.axios.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        if (typeof window !== 'undefined') {
          const token = localStorage.getItem('access_token');
          if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.axios.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        if (error.response?.status === 401) {
          if (typeof window !== 'undefined') {
            localStorage.removeItem('access_token');
            localStorage.removeItem('auth-storage');
            window.alert('세션이 만료되었습니다. 로그인 페이지로 이동합니다.');
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * 서버 응답의 { data: T } 래퍼를 자동으로 벗겨 T를 반환합니다.
   *
   * 서버 컨벤션:
   *   목록: { data: { items: [], total: 0 } }  →  { items: [], total: 0 }
   *   상세: { data: { id: 1, name: "..." } }   →  { id: 1, name: "..." }
   */
  private unwrapResponse<T>(body: unknown): T {
    if (body && typeof body === 'object' && 'data' in body) {
      return (body as { data: T }).data;
    }
    return body as T;
  }

  private handleError(error: unknown, url: string, method: string): never {
    if (isAxiosError(error)) {
      if (error.response?.status === 401) throw error; // interceptor가 처리

      const responseData = error.response?.data as ServerErrorResponseData | undefined;
      const serverMessage =
        responseData?.data?.message ||
        responseData?.msg ||
        responseData?.message;

      if (error.response?.status === 404) {
        throw new NotFoundError(serverMessage ?? undefined);
      }

      const message = serverMessage || `[${method} ${url}] 요청에 실패했습니다.`;
      console.warn(`[ApiService] ${method} ${url}:`, message);
      throw new Error(message);
    }

    throw error;
  }

  // ─── HTTP 메서드 — 모두 Promise<T> 직접 반환 ─────────────────────────────
  //
  // Before: apiService.get<{ data: { items: T[] } }>(url)
  //           → result.response.data.data.items  (4단계 접근)
  //
  // After:  apiService.get<PaginatedResponse<T>>(url)
  //           → result.items                     (1단계 접근)

  public async get<T = unknown>(url: string, params: object = {}, options: AxiosRequestConfig = {}): Promise<T> {
    try {
      const response = await this.axios.get<unknown>(url, {
        ...options,
        params, // 위에서 받은 params 전달
        paramsSerializer: {
          serialize: (paramObj) => {
            const searchParams = new URLSearchParams();

            Object.entries(paramObj).forEach(([key, value]) => {
              if (Array.isArray(value)) {
                // 배열인 경우: 같은 키로 여러 번 append 합니다.
                // 결과: statuses=active&statuses=pending&statuses=suspend
                value.forEach(v => {
                  if (v !== undefined && v !== null) {
                    searchParams.append(key, String(v));
                  }
                });
              } else if (value !== undefined && value !== null) {
                // 일반 값인 경우
                searchParams.append(key, String(value));
              }
            });

            return searchParams.toString();
          }
        }
      });

      return this.unwrapResponse<T>(response.data);
    } catch (error) {
      return this.handleError(error, url, 'GET');
    }
  }

  public async post<T = unknown>(url: string, data: unknown = {}, options: AxiosRequestConfig = {}): Promise<T> {
    try {
      const response = await this.axios.post<unknown>(url, data, options);
      return this.unwrapResponse<T>(response.data);
    } catch (error) {
      return this.handleError(error, url, 'POST');
    }
  }

  public async patch<T = unknown>(url: string, data: unknown = {}, options: AxiosRequestConfig = {}): Promise<T> {
    try {
      const response = await this.axios.patch<unknown>(url, data, options);
      return this.unwrapResponse<T>(response.data);
    } catch (error) {
      return this.handleError(error, url, 'PATCH');
    }
  }

  public async put<T = unknown>(url: string, data: unknown = {}, options: AxiosRequestConfig = {}): Promise<T> {
    try {
      const response = await this.axios.put<unknown>(url, data, options);
      return this.unwrapResponse<T>(response.data);
    } catch (error) {
      return this.handleError(error, url, 'PUT');
    }
  }

  public async delete<T = unknown>(url: string, data: unknown = {}, options: AxiosRequestConfig = {}): Promise<T> {
    try {
      const response = await this.axios.delete<unknown>(url, { data, ...options });
      return this.unwrapResponse<T>(response.data);
    } catch (error) {
      return this.handleError(error, url, 'DELETE');
    }
  }
}

export const apiService = new ApiService();